import { Aside, Crumbs } from '@components';
import { useScrollContext } from '@contextProviders';
import { RootState, clearAside, clearAsides, switchAside } from '@stores';
import React from 'react';
import { X } from 'react-feather';
import { useDispatch, useSelector } from 'react-redux';

const AnimatedAside: React.FC<{
  width: string;
  children: React.ReactNode;
}> = ({ width, children }) => {
  const [mounted, setMounted] = React.useState(false);

  React.useEffect(() => {
    setMounted(true);
  }, []);

  return (
    <div
      className="
        relative right-0 h-full flex-shrink-0
        transition-all duration-300 ease-out
      "
      style={{
        width,
        opacity: mounted ? 1 : 0,
        transform: mounted ? 'translateX(0)' : 'translateX(40px)',
      }}
    >
      {children}
    </div>
  );
};

const asideClasses = [
  'z-50',
  'fixed',
  'right-0',
  'top-0',
  'h-full',
  'flex',
  'flex-row',
  'items-stretch',
  'overflow-hidden',
  'transition-all',

  // Glassmorphism
  'bg-white/40',
  'dark:bg-gray-900/80',
  'dark:text-slate-300',
  'text-slate-900',
  'border-l',
  'border-white/20',
  'dark:border-gray-800/50',
];

// helper: "950px" -> 950
const pxToNumber = (px?: string, fallback = 950) => {
  if (!px) return fallback;
  const n = Number(px.replace('px', ''));
  return Number.isFinite(n) ? n : fallback;
};

export const AsideManager = () => {
  const dispatch = useDispatch();
  const asideStore = useSelector((state: RootState) => state.aside);
  const { scrollRef } = useScrollContext();

  if (!asideStore.root || asideStore.root.hide) {
    return null;
  }

  const components = [asideStore.root];
  let current = asideStore.root;
  while (current.child) {
    components.push(current.child);
    current = current.child;
  }

  const closeAllAsides = () => dispatch(clearAsides());
  const closeGivenAside = (title: string) => dispatch(clearAside(title));
  const toggleCrumb = (title: string) => dispatch(switchAside(title));

  // ✅ bepaal het actieve (focused) panel
  const active =
    components.find((c) => c.focused) ?? components[components.length - 1];

  const activeWidth = active.customWidth ?? '950px';
  const activeWidthPx = pxToNumber(activeWidth, 950);

  // ✅ backdrop moet rekening houden met actieve width (niet "totalWidth")
  const backdropWidth = `calc(100% - ${activeWidthPx}px)`;

  return (
    <>
      <button
        className="
          z-40 fixed top-0 left-0 h-full
          bg-black/30
          transition-opacity duration-300
          hidden md:block
        "
        style={{ width: backdropWidth }}
        onClick={closeAllAsides}
      />

      {/* ✅ aside shell volgt actieve breedte */}
      <aside
        ref={scrollRef}
        className={asideClasses.join(' ')}
        style={{ width: activeWidth }}
      >
        {/* ✅ AnimatedAside maar 1x, panels blijven gemount */}
        <AnimatedAside width={activeWidth}>
          <div className="relative h-full w-full">
            {components.map((component) => {
              const isActive = component.title === active.title;

              return (
                <div
                  key={component.title}
                  className={`
                    absolute inset-0 h-full
                    backdrop-blur-xl
                    overflow-auto px-20 py-10
                    transition-all duration-300 ease-out
                    shadow-2xl
                    ${
                      isActive
                        ? 'opacity-100 translate-x-0 pointer-events-auto'
                        : 'opacity-0 translate-x-6 blur-sm pointer-events-none'
                    }
                  `}
                  aria-hidden={!isActive}
                >
                  {/* ✅ close op actieve */}
                  {isActive && (
                    <button
                      onClick={() => closeGivenAside(component.title)}
                      className="absolute right-4 top-4 z-10 text-red-600"
                    >
                      <X />
                    </button>
                  )}

                  {/* ✅ crumbs op actieve */}
                  {isActive && components.length > 1 && (
                    <Crumbs
                      links={components}
                      onclick={(title: string) => toggleCrumb(title)}
                      current={components[0].title}
                    />
                  )}

                  {/* ✅ ALWAYS mounted: geen {component.focused && ...} */}
                  <Aside
                    callback={component.callback}
                    component={component.component}
                    title={component.title}
                    data={{ ...component.data, title: component.title }}
                  />
                </div>
              );
            })}
          </div>
        </AnimatedAside>
      </aside>
    </>
  );
};

export default AsideManager;
